import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBarEntry } from '@nucleus-commerce/admin-kit/save-bar';
import type { ContentBlock, ContentSectionBlocksItem } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useSetSectionBlocks } from '../hooks/use-section-mutations';

import { AddSectionBlocksDialog } from './add-section-blocks-dialog';
import { BlockKindBadge, BlockScopeBadge, ContentStatusBadge } from './content-badges';

export interface SectionBlocksSectionProps {
  storeId: string;
  sectionId: string;
  /** The saved list; local edits start from it and are sent back with one PUT. */
  items: readonly ContentSectionBlocksItem[];
  canWrite: boolean;
}

const ids = (rows: readonly ContentSectionBlocksItem[]) => rows.map((r) => r.blockId).join('\n');

function move(
  rows: ContentSectionBlocksItem[],
  from: number,
  to: number,
): ContentSectionBlocksItem[] {
  if (to < 0 || to >= rows.length) return rows;
  const next = [...rows];
  const [row] = next.splice(from, 1);
  if (row) next.splice(to, 0, row);
  return next.map((r, i) => ({ ...r, position: i }));
}

/** Ordered block list of a section: add from the store's and shared blocks, move up / down, remove, save with one PUT. */
export function SectionBlocksSection({
  storeId,
  sectionId,
  items,
  canWrite,
}: SectionBlocksSectionProps) {
  const { t } = useTranslation(['content', 'errors']);
  const message = useErrorMessage();
  const save = useSetSectionBlocks(storeId);
  const [rows, setRows] = useState<ContentSectionBlocksItem[]>([...items]);
  const [baseline, setBaseline] = useState(items);
  const [seen, setSeen] = useState(items);
  if (seen !== items) {
    const wasDirty = ids(rows) !== ids(baseline);
    setSeen(items);
    setBaseline(items);
    if (!wasDirty) setRows([...items]);
  }
  const dirty = ids(rows) !== ids(baseline);

  const add = (added: ContentBlock[]) =>
    setRows((current) => {
      const known = new Set(current.map((r) => r.blockId));
      const fresh = added
        .filter((block) => !known.has(block.id))
        .map((block, i): ContentSectionBlocksItem => ({
          blockId: block.id,
          key: block.key,
          kind: block.kind,
          name: block.name,
          status: block.status,
          scope: block.scope,
          position: current.length + i,
        }));
      return [...current, ...fresh];
    });

  const remove = (blockId: string) =>
    setRows((current) =>
      current.filter((r) => r.blockId !== blockId).map((r, i) => ({ ...r, position: i })),
    );

  const saveBlocks = () =>
    save.mutate(
      { id: sectionId, blockIds: rows.map((r) => r.blockId) },
      {
        onSuccess: (saved) => {
          setBaseline(saved.blocks);
          setRows([...saved.blocks]);
        },
      },
    );

  const discard = () => setRows([...baseline]);

  useSaveBarEntry({
    dirty,
    saving: save.isPending,
    onSave: saveBlocks,
    onDiscard: discard,
    disabled: !canWrite,
  });

  return (
    <FormSection
      title={t('content:sections.blocks')}
      description={t('content:sections.blocksDescription')}
      tour="content.section-editor.blocks"
    >
      {canWrite ? (
        <div className="flex flex-wrap items-center gap-2">
          <AddSectionBlocksDialog
            storeId={storeId}
            excludeIds={new Set(rows.map((r) => r.blockId))}
            onAdd={add}
            disabled={save.isPending}
          />
          <span className="flex-1" />
          <Button
            type="button"
            size="sm"
            disabled={!dirty || save.isPending}
            data-tour="content.section-editor.save-blocks"
            onClick={saveBlocks}
          >
            {save.isPending ? t('content:sectionBlocks.saving') : t('content:sectionBlocks.save')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={!dirty || save.isPending}
            onClick={discard}
          >
            {t('content:actions.discard')}
          </Button>
        </div>
      ) : null}
      {save.isError ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {message(save.error)}
            {isApiError(save.error) && save.error.requestId ? (
              <span className="block text-xs">
                {t('errors:requestIdShort', { requestId: save.error.requestId })}
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('content:sectionBlocks.empty')}</p>
      ) : (
        <ol className="divide-y rounded-md border" aria-label={t('content:sections.blocks')}>
          {rows.map((item, index) => (
            <li key={item.blockId} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="text-muted-foreground w-6 shrink-0 text-right tabular-nums">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                {item.scope === 'shared' ? (
                  <Link
                    to="/org/shared-blocks/$blockId"
                    params={{ blockId: item.blockId }}
                    className="block truncate font-medium hover:underline"
                  >
                    {item.name}
                  </Link>
                ) : (
                  <Link
                    to="/s/$storeId/content/blocks/$blockId"
                    params={{ storeId, blockId: item.blockId }}
                    className="block truncate font-medium hover:underline"
                  >
                    {item.name}
                  </Link>
                )}
                <span className="text-muted-foreground block truncate font-mono text-[11px]">
                  {item.key}
                </span>
              </div>
              <span className="hidden items-center gap-1 sm:flex">
                <BlockKindBadge kind={item.kind} />
                <BlockScopeBadge scope={item.scope} />
              </span>
              <ContentStatusBadge status={item.status} />
              {canWrite ? (
                <span className="flex shrink-0 items-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    disabled={index === 0}
                    onClick={() => setRows((current) => move(current, index, index - 1))}
                    aria-label={t('content:sectionBlocks.moveUp', { name: item.name })}
                  >
                    <ArrowUp aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    disabled={index === rows.length - 1}
                    onClick={() => setRows((current) => move(current, index, index + 1))}
                    aria-label={t('content:sectionBlocks.moveDown', { name: item.name })}
                  >
                    <ArrowDown aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => remove(item.blockId)}
                    aria-label={t('content:sectionBlocks.remove', { name: item.name })}
                  >
                    <X aria-hidden />
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </FormSection>
  );
}
